import React, { useEffect, useState } from 'react';
import { Image, PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { twMerge } from 'tailwind-merge';
import { IconStar } from '@src/assets/icon-star';
import { TvChannel } from '@src/lib/graphql/generated';
import { Collection } from '@lib/schema';
import { Genre, Provider } from '@lib/streaming';
import { IconNext } from '@assets/icon-next';
import { IconPrev } from '@assets/icon-prev';

export interface EntryCarouselProps extends PropsWithClassProps<'entry' | 'entryContainer' | 'carouselRoot'> {
  entries: Genre[] | Provider[] | TvChannel[] | Collection[];
  onEntryChange?: (Item) => void;
  type?: 'text' | 'image';
  primaryProperty: string;
  altProperty?: string;
  target?: string;
  isTvChannels?: boolean;
}

export const EntryCarousel = ({
  entries,
  type = 'text',
  target,
  primaryProperty,
  altProperty,
  onEntryChange,
  isTvChannels,
  classProps,
}: EntryCarouselProps) => {
  const [activeEntryIndex, setActiveEntryIndex] = useState(0);
  const [directMove, setDirectMove] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const entryItems = [].concat(entries);

  useEffect(() => {
    setLoaded(true);
    if (!target) return;
    const targetIndex = entryItems.findIndex((e) => e?.id === target);

    const moveCount = targetIndex - activeEntryIndex;
    setDirectMove(true);
    changeActiveIndex(moveCount);
    setTimeout(() => {
      setDirectMove(false);
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!entryItems || entryItems.length < 1) return;
  const originalLength = entryItems.length;
  if (originalLength < 5 && entryItems) {
    const addCount = 5 - originalLength + (originalLength % 2 === 0 ? 1 : 0);
    for (let i = 0; i < addCount; i++) {
      entryItems.push(entryItems[i % originalLength]);
    }
  }

  const outerLeftIndex =
    activeEntryIndex - 2 < -1
      ? entryItems.length - 2
      : activeEntryIndex - 2 < 0
        ? entryItems.length - 1
        : activeEntryIndex - 2;
  const leftIndex = activeEntryIndex - 1 < 0 ? entryItems.length - 1 : activeEntryIndex - 1;
  const rightIndex = (activeEntryIndex + 1) % entryItems.length;
  const outerRightIndex = (activeEntryIndex + 2) % entryItems.length;

  const changeActiveIndex = (Count) => {
    const target =
      activeEntryIndex + Count >= entryItems.length
        ? (activeEntryIndex + Count) % entryItems.length
        : activeEntryIndex + Count < 0
          ? activeEntryIndex + Count + entryItems.length
          : activeEntryIndex + Count;
    setActiveEntryIndex(target);
    onEntryChange(entryItems[target]);
  };

  const styles = {
    base: twMerge(
      'absolute inset-0 rounded-lg origin-center text-white font-herokid text-center grid place-items-center hyphenate ',
      directMove ? 'transition-none' : 'transition duration-300',
    ),
    outerLeft: 'translate-x-[-100%] md:translate-x-[-150%] scale-25 opacity-0',
    left: 'translate-x-[-75%] md:translate-x-[-100%] scale-50 opacity-50',
    center: 'z-10',
    right: 'translate-x-[75%] md:translate-x-[100%] scale-50 opacity-50',
    outerRight: 'translate-x-[100%] md:translate-x-[150%] scale-25 opacity-0',
  };

  return (
    <div
      className={twMerge(
        'group relative grid min-h-0 w-full items-center gap-4 overflow-hidden sm:grid-cols-[auto,1fr,auto]',
        classProps?.root,
      )}
    >
      <div className="absolute inset-y-0 left-6 z-30 grid items-center transition-opacity duration-150 group-hover:opacity-100 sm:static pointer:opacity-50">
        <button
          role="button"
          aria-label="slide backwards"
          className="grid size-16 cursor-pointer place-items-center rounded-full border border-gray-650/65 bg-gray-820/70 text-gray-650/65 transition-all duration-300 hover:border-gray-400/75 hover:text-gray-400/75"
          onClick={() => {
            changeActiveIndex(-1);
          }}
        >
          <IconPrev classProps={{ root: 'w-7 h-4' }} />
        </button>
      </div>
      <div className="block size-full">
        <div
          className={twMerge(
            'grid h-full w-full items-center justify-center',
            type !== 'text' && 'aspect-video md:aspect-auto',
          )}
        >
          <div
            className={twMerge(
              'relative aspect-video h-full min-h-full w-full min-w-full opacity-0 transition-opacity duration-200 md:aspect-square md:h-[40vh]',
              loaded && 'opacity-100',
              classProps?.carouselRoot,
            )}
          >
            {entryItems?.map((entry, index) => (
              <div
                className={twMerge(
                  styles.base,
                  index === outerLeftIndex
                    ? styles.outerLeft
                    : index === leftIndex
                      ? styles.left
                      : index === activeEntryIndex
                        ? styles.center
                        : index === rightIndex
                          ? styles.right
                          : index === outerRightIndex
                            ? styles.outerRight
                            : 'opacity-0',
                )}
                key={entry?.id + index}
              >
                <div className={classProps?.entryContainer}>
                  {isTvChannels && (
                    <div
                      className={twMerge(
                        'absolute top-0 h-2 w-full bg-gray-500 transition duration-100',
                        entry?.favorite?.id
                          ? 'bg-primary text-primary'
                          : 'text-gray-500 text-opacity-50 hover:text-primary hover:text-opacity-100',
                      )}
                    >
                      <div className="absolute right-0 top-0 mt-3 cursor-pointer p-4">
                        <IconStar classProps={{ root: 'size-6 stroke-2' }} filled={!!entry?.favorite?.id} />
                      </div>
                    </div>
                  )}
                  {type === 'text' ? (
                    <span className={classProps?.entry}>{entry?.[primaryProperty]}</span>
                  ) : (
                    <div className={classProps?.entry}>
                      {entry?.[primaryProperty] ? (
                        <Image
                          src={entry?.[primaryProperty]}
                          alt={entry?.[altProperty]}
                          fill
                          sizes="300px"
                          copyright=""
                        />
                      ) : (
                        <span>{entry?.[altProperty]}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute inset-y-0 right-6 z-30 grid items-center transition-opacity duration-150 group-hover:opacity-100 sm:static pointer:opacity-20">
        <button
          role="button"
          aria-label="slide forward"
          className="grid size-16 cursor-pointer place-items-center rounded-full border border-gray-650/65 bg-gray-820/70 text-gray-650/65 transition-all duration-300 hover:border-gray-400/75 hover:text-gray-400/75"
          onClick={() => {
            changeActiveIndex(1);
          }}
        >
          <IconNext classProps={{ root: 'w-7 h-4' }} />
        </button>
      </div>
      <div className="absolute inset-y-0 -left-16 w-1/3 bg-gradient-to-r from-black via-black/80 to-black/0"></div>
      <div className="absolute inset-y-0 -right-16 w-1/3 bg-gradient-to-l from-black via-black/80 to-black/0"></div>
      <div className="radial-carousel-switcher-background absolute inset-x-[10%] inset-y-0"></div>
    </div>
  );
};
